import { makeAutoObservable } from "mobx";

import { createNutritionItem, addTotals, createEmptyTotals, getFiniteNumber } from "./NutritionItems";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

const GOAL_MACRO_INPUT_MODES = new Set(["grams", "percent"]);
const GOAL_PERCENTAGE_IDS = new Set(["protein", "carbs", "fats"]);

function normalizeGoalEntries(entries, { allowedIds = null } = {}) {
  const normalizedEntries = Array.isArray(entries)
    ? entries.map((entry) => ({
        id: String(entry?.id ?? "").trim(),
        value: getFiniteNumber(entry?.value, 0),
      }))
    : entries && typeof entries === "object"
      ? Object.keys(entries).map((id) => ({
          id: String(id ?? "").trim(),
          value: getFiniteNumber(entries[id], 0),
        }))
      : [];

  return normalizedEntries
    .filter((entry) => {
      if (!entry.id) {
        return false;
      }

      return allowedIds == null || allowedIds.has(entry.id);
    });
}

function normalizeGoalMacroInputMode(goals) {
  const normalizedMode = String(
    goals?.macro_input_mode ?? goals?.macroInputMode ?? "grams",
  )
    .trim()
    .toLowerCase();

  return GOAL_MACRO_INPUT_MODES.has(normalizedMode) ? normalizedMode : "grams";
}

function normalizeGoalNutrients(goals) {
  return normalizeGoalEntries(goals?.nutrients);
}

function normalizeGoalPercentages(goals) {
  return normalizeGoalEntries(goals?.percentages, {
    allowedIds: GOAL_PERCENTAGE_IDS,
  });
}

function normalizeGoals(goals) {
  return {
    macro_input_mode: normalizeGoalMacroInputMode(goals),
    nutrients: normalizeGoalNutrients(goals),
    percentages: normalizeGoalPercentages(goals),
  };
}

function hasExplicitGoalModeSelection(goals) {
  return (
    goals != null &&
    typeof goals === "object" &&
    (Object.prototype.hasOwnProperty.call(goals, "macro_input_mode") ||
      Object.prototype.hasOwnProperty.call(goals, "macroInputMode"))
  );
}

function normalizeSummaryTotals(summaryTotals) {
  const source = summaryTotals && typeof summaryTotals === "object" ? summaryTotals : {};

  return {
    calories: getFiniteNumber(source.calories, 0),
    carbs: getFiniteNumber(source.carbs, 0),
    protein: getFiniteNumber(source.protein, 0),
    fats: getFiniteNumber(source.fats, 0),
    fiber: getFiniteNumber(source.fiber, 0),
    micros: createEmptyTotals().micros,
  };
}

function getMealOptionPreviewName(item) {
  if (item?.type === "equivalence") {
    return String(item?.options?.[0]?.name ?? item?.name ?? "").trim();
  }

  return String(item?.name ?? "").trim();
}

export class CoachNutritionPlanMealOption {
  constructor(data = {}) {
    Object.assign(this, data);
    this.items = (Array.isArray(data?.items) ? data.items : []).map(createNutritionItem);
    makeAutoObservable(this);
  }

  get totals() {
    return this.items.reduce((totals, item) => addTotals(totals, item?.totals), createEmptyTotals());
  }

  get foodsPreview() {
    return this.items
      .map(getMealOptionPreviewName)
      .filter(Boolean)
      .slice(0, 3);
  }

  get isValid() {
    return this.name?.trim?.().length > 0 && this.items.length > 0;
  }

  addFoodItem(item) {
    this.items = [...this.items, createNutritionItem(item)];
  }

  addRecipeItem(item) {
    this.items = [...this.items, createNutritionItem(item)];
  }

  addEquivalenceItem(item) {
    this.items = [...this.items, createNutritionItem(item)];
  }

  removeItem(itemId) {
    this.items = this.items.filter((item) => item?.id !== itemId);
  }

  reorderItems(nextItems) {
    this.items = (Array.isArray(nextItems) ? nextItems : []).map(createNutritionItem);
  }

  toFirestore() {
    return {
      id: String(this.id ?? "").trim().toLowerCase(),
      name: String(this.name ?? "").trim(),
      items: this.items.map((item) => ({ ...item })),
    };
  }
}

export class CoachNutritionPlanMeal {
  constructor(data = {}) {
    this.id = "";
    this.name = "";
    this.notes = "";
    this.order = 0;
    this.day_id = "";
    this.day_name = "";
    this.options = [];
    Object.assign(this, data);
    this.options = (Array.isArray(data?.options) ? data.options : []).map(
      (option) => new CoachNutritionPlanMealOption(option),
    );
    makeAutoObservable(this);
  }

  get defaultOption() {
    return this.options[0] ?? null;
  }

  get totals() {
    return this.defaultOption?.totals ?? createEmptyTotals();
  }

  get isEmpty() {
    return !this.defaultOption || this.defaultOption.items.length === 0;
  }

  addOption(option) {
    this.options = [...this.options, new CoachNutritionPlanMealOption(option)];
  }

  removeOption(optionId) {
    this.options = this.options.filter((option) => option.id !== optionId);
  }

  reorderOptions(nextOptions) {
    this.options = (Array.isArray(nextOptions) ? nextOptions : []).map(
      (option) => new CoachNutritionPlanMealOption(option),
    );
  }

  toMealDoc() {
    const record = {
      id: String(this.id ?? "").trim().toLowerCase(),
      name: String(this.name ?? "").trim(),
      order: getFiniteNumber(this.order, 0),
      options: this.options.map((option) => option.toFirestore()),
    };

    if (hasText(this.notes)) {
      record.notes = String(this.notes).trim();
    }

    if (hasText(this.day_id)) {
      record.day_id = String(this.day_id).trim();
    }

    if (hasText(this.day_name)) {
      record.day_name = String(this.day_name).trim();
    }

    return record;
  }
}

export class CoachNutritionPlan {
  constructor(data = {}) {
    Object.assign(this, data);
    this._rev = getFiniteNumber(data?._rev, 0);
    this.goals = normalizeGoals(data?.goals);
    this.hasExplicitGoalMode = hasExplicitGoalModeSelection(data?.goals);
    this.summary_totals = normalizeSummaryTotals(data?.summary_totals);
    this.meal_count = getFiniteNumber(data?.meal_count, 0);
    this.meals = (Array.isArray(data?.meals) ? data.meals : []).map(
      (meal) => new CoachNutritionPlanMeal(meal),
    );
    makeAutoObservable(this);
  }

  static fromFirestore(planRecord, mealRecords = []) {
    return new CoachNutritionPlan({
      ...planRecord,
      meals: mealRecords,
      createdByMe: true,
    });
  }

  get sortedMeals() {
    return [...this.meals].sort(
      (leftMeal, rightMeal) => getFiniteNumber(leftMeal?.order, 0) - getFiniteNumber(rightMeal?.order, 0),
    );
  }

  get totals() {
    if (this.sortedMeals.length === 0) {
      return normalizeSummaryTotals(this.summary_totals);
    }

    return this.sortedMeals.reduce((totals, meal) => addTotals(totals, meal.totals), createEmptyTotals());
  }

  get calories() {
    return this.totals.calories;
  }

  get carbs() {
    return this.totals.carbs;
  }

  get protein() {
    return this.totals.protein;
  }

  get fats() {
    return this.totals.fats;
  }

  get goalsMap() {
    return this.goals.nutrients.reduce((map, entry) => {
      map[entry.id] = entry.value;
      return map;
    }, {});
  }

  get goalsPercentagesMap() {
    return this.goals.percentages.reduce((map, entry) => {
      map[entry.id] = entry.value;
      return map;
    }, {});
  }

  get detail() {
    const mealCount =
      this.sortedMeals.length > 0
        ? this.sortedMeals.length
        : getFiniteNumber(this.meal_count, 0);
    return `${mealCount} meal${mealCount === 1 ? "" : "s"}`;
  }

  get isValid() {
    return this.name?.trim?.().length > 0 && this.sortedMeals.length > 0;
  }

  get nextVersion() {
    return Math.max(1, getFiniteNumber(this.version, 0) + 1);
  }

  addMeal(meal) {
    this.meals = [...this.meals, new CoachNutritionPlanMeal(meal)];
  }

  removeMeal(mealId) {
    this.meals = this.meals.filter((meal) => meal.id !== mealId);
  }

  reorderMeals(nextMeals) {
    this.meals = (Array.isArray(nextMeals) ? nextMeals : []).map(
      (meal) => new CoachNutritionPlanMeal(meal),
    );
  }

  toPlanDoc(options = {}) {
    const normalizedGoals = normalizeGoals(this.goals);
    const summaryTotals = this.sortedMeals.length > 0
      ? this.totals
      : normalizeSummaryTotals(options.summaryTotals ?? this.summary_totals);
    const mealCount =
      options.mealCount !== undefined
        ? getFiniteNumber(options.mealCount, 0)
        : this.sortedMeals.length > 0
          ? this.sortedMeals.length
          : getFiniteNumber(this.meal_count, 0);
    const record = {
      schema_version: "nutrition_plan_v1",
      id: String(this.id ?? "").trim().toLowerCase(),
      name: String(this.name ?? "").trim(),
      version: Math.max(1, getFiniteNumber(this.version, 1)),
      meal_count: mealCount,
      summary_totals: {
        calories: getFiniteNumber(summaryTotals.calories, 0),
        carbs: getFiniteNumber(summaryTotals.carbs, 0),
        protein: getFiniteNumber(summaryTotals.protein, 0),
        fats: getFiniteNumber(summaryTotals.fats, 0),
        fiber: getFiniteNumber(summaryTotals.fiber, 0),
      },
    };

    if (hasText(this.notes)) {
      record.notes = String(this.notes).trim();
    }

    if (
      normalizedGoals.nutrients.length > 0 ||
      normalizedGoals.percentages.length > 0 ||
      normalizedGoals.macro_input_mode === "percent" ||
      (normalizedGoals.macro_input_mode === "grams" &&
        this.hasExplicitGoalMode === true)
    ) {
      record.goals = {
        macro_input_mode: normalizedGoals.macro_input_mode,
        nutrients: normalizedGoals.nutrients,
      };

      if (normalizedGoals.percentages.length > 0) {
        record.goals.percentages = normalizedGoals.percentages;
      }
    }

    if (options.createdAt !== undefined) {
      record.createdAt = options.createdAt;
    }

    if (options.updatedAt !== undefined) {
      record.updatedAt = options.updatedAt;
    }

    const revision = getFiniteNumber(options.rev ?? this._rev, null);
    if (revision != null) {
      record._rev = revision;
    }

    return record;
  }

  toMealDocs() {
    return this.sortedMeals.map((meal) => meal.toMealDoc());
  }
}

export default CoachNutritionPlan;
